import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useWizardStore, useWizardActions } from '@/stores';
import {
  makeNewOrganizationSchema,
  type NewOrganizationFormData,
  formatPhoneNumber,
} from '@/lib/validations';
import { useAuth } from '@/hooks';
import { hasPermission } from '@/types';

const BILLING_OPTIONS = [
  { value: 'easyTithe', label: 'easyTithe' },
  { value: 'Legacy', label: 'Legacy' },
];

export function NewOrganizationForm() {
  const organizationData = useWizardStore((s) => s.organizationData);
  const { setOrganizationData, setIsValid } = useWizardActions();

  // EUID is optional for admins, required for operators
  const { user, isLoadingRoles } = useAuth();
  const isAdmin = user?.roles?.some((role) => hasPermission(role, 'admin')) ?? false;

  const resolver = useMemo(
    () => zodResolver(makeNewOrganizationSchema({ euidRequired: !isAdmin })),
    [isAdmin]
  );

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isValid },
    setValue,
  } = useForm<NewOrganizationFormData>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      organizationName: organizationData?.organizationName ?? '',
      streetAddress: organizationData?.streetAddress ?? '',
      addressLine2: organizationData?.addressLine2 ?? '',
      city: organizationData?.city ?? '',
      state: organizationData?.state ?? '',
      zipCode: organizationData?.zipCode ?? '',
      ownerFirstName: organizationData?.ownerFirstName ?? '',
      ownerLastName: organizationData?.ownerLastName ?? '',
      ownerEmail: organizationData?.ownerEmail ?? '',
      ownerPhone: organizationData?.ownerPhone ?? '',
      billing: organizationData?.billing === 'easyTithe' || organizationData?.billing === 'Legacy'
        ? organizationData.billing
        : 'easyTithe',
      sendInvite: organizationData?.sendInvite ?? true,
      euid: organizationData?.euid ?? '',
    },
  });

  // Roles resolve asynchronously — the resolver may have been built before the
  // user's role was known. Reconcile the stored validity against the role-correct
  // schema without trigger(), which would surface errors on a pristine form.
  useEffect(() => {
    if (!isLoadingRoles) {
      const ok = makeNewOrganizationSchema({ euidRequired: !isAdmin })
        .safeParse(getValues()).success;
      setIsValid(ok);
    }
  }, [isLoadingRoles, isAdmin, getValues, setIsValid]);

  // Sync form values to store via watch subscription (avoids new object ref each render)
  useEffect(() => {
    const subscription = watch((formValues) => {
      setOrganizationData({
        organizationType: 'New',
        organizationName: formValues.organizationName ?? '',
        streetAddress: formValues.streetAddress ?? '',
        addressLine2: formValues.addressLine2 ?? '',
        city: formValues.city ?? '',
        state: formValues.state ?? '',
        zipCode: formValues.zipCode ?? '',
        ownerFirstName: formValues.ownerFirstName ?? '',
        ownerLastName: formValues.ownerLastName ?? '',
        ownerEmail: formValues.ownerEmail ?? '',
        ownerPhone: formValues.ownerPhone ?? '',
        billing: formValues.billing ?? '',
        sendInvite: formValues.sendInvite ?? true,
        euid: formValues.euid ?? '',
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, setOrganizationData]);

  // Sync form validity to store
  useEffect(() => {
    setIsValid(isValid);
  }, [isValid, setIsValid]);

  // Format phone number on blur
  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('ownerPhone', formatted, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(() => {})} className="space-y-6">
      {/* Organization Info */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="organizationName">
            Organization Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="organizationName"
            {...register('organizationName')}
            aria-invalid={!!errors.organizationName}
            aria-describedby={errors.organizationName ? 'org-name-error' : undefined}
          />
          {errors.organizationName && (
            <p id="org-name-error" className="text-sm text-destructive mt-1">
              {errors.organizationName.message}
            </p>
          )}
        </div>
      </div>

      <Separator />

      {/* Address */}
      <div className="space-y-4">
        <h4 className="font-medium">Address</h4>

        <div>
          <Label htmlFor="streetAddress">
            Street Address <span className="text-destructive">*</span>
          </Label>
          <Input
            id="streetAddress"
            {...register('streetAddress')}
            aria-invalid={!!errors.streetAddress}
          />
          {errors.streetAddress && (
            <p className="text-sm text-destructive mt-1">{errors.streetAddress.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="addressLine2">Address Line 2</Label>
          <Input id="addressLine2" {...register('addressLine2')} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-2">
            <Label htmlFor="city">
              City <span className="text-destructive">*</span>
            </Label>
            <Input
              id="city"
              {...register('city')}
              aria-invalid={!!errors.city}
            />
            {errors.city && (
              <p className="text-sm text-destructive mt-1">{errors.city.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="state">
              State <span className="text-destructive">*</span>
            </Label>
            <Input
              id="state"
              {...register('state', {
                onChange: (e) => {
                  // The input is visually uppercased via CSS (`className="uppercase"`),
                  // but the form value stored by RHF reflects whatever the user typed.
                  // Normalize to uppercase here so Zod's `US_STATES.includes(val)`
                  // refine — and the wizard store sync via `watch` — both see the
                  // canonical 2-letter code instead of lowercase input.
                  const value = (e.target as HTMLInputElement).value;
                  const upper = value.toUpperCase();
                  if (value !== upper) {
                    setValue('state', upper, { shouldValidate: true });
                  }
                },
              })}
              maxLength={2}
              placeholder="TX"
              className="uppercase"
              aria-invalid={!!errors.state}
            />
            {errors.state && (
              <p className="text-sm text-destructive mt-1">{errors.state.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="zipCode">
              Zip Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="zipCode"
              {...register('zipCode')}
              placeholder="12345"
              aria-invalid={!!errors.zipCode}
            />
            {errors.zipCode && (
              <p className="text-sm text-destructive mt-1">{errors.zipCode.message}</p>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Owner Info */}
      <div className="space-y-4">
        <h4 className="font-medium">Organization Owner</h4>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ownerFirstName">
              First Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ownerFirstName"
              {...register('ownerFirstName')}
              aria-invalid={!!errors.ownerFirstName}
            />
            {errors.ownerFirstName && (
              <p className="text-sm text-destructive mt-1">{errors.ownerFirstName.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="ownerLastName">
              Last Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ownerLastName"
              {...register('ownerLastName')}
              aria-invalid={!!errors.ownerLastName}
            />
            {errors.ownerLastName && (
              <p className="text-sm text-destructive mt-1">{errors.ownerLastName.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ownerEmail">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ownerEmail"
              type="email"
              {...register('ownerEmail')}
              aria-invalid={!!errors.ownerEmail}
            />
            {errors.ownerEmail && (
              <p className="text-sm text-destructive mt-1">{errors.ownerEmail.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="ownerPhone">Phone</Label>
            <Input
              id="ownerPhone"
              type="tel"
              {...register('ownerPhone')}
              onBlur={handlePhoneBlur}
              placeholder="(123) 456-7890"
              aria-invalid={!!errors.ownerPhone}
            />
            {errors.ownerPhone && (
              <p className="text-sm text-destructive mt-1">{errors.ownerPhone.message}</p>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Billing & Options */}
      <div className="space-y-4">
        <h4 className="font-medium">Billing & Options</h4>

        <div>
          <Label htmlFor="billing">
            Billing Type <span className="text-destructive">*</span>
          </Label>
          <select
            id="billing"
            {...register('billing')}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-invalid={!!errors.billing}
          >
            {BILLING_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.billing && (
            <p className="text-sm text-destructive mt-1">{errors.billing.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="sendInvite"
            {...register('sendInvite')}
            className="h-4 w-4 rounded border-input"
          />
          <Label htmlFor="sendInvite" className="font-normal">
            Send welcome email to organization owner
          </Label>
        </div>

        <div>
          <Label htmlFor="euid">
            EUID {!isAdmin && <span className="text-destructive">*</span>}
          </Label>
          <Input
            id="euid"
            {...register('euid')}
            placeholder="MB_XXXXXXXXXXXXXXXXXX"
            className="font-mono"
            aria-invalid={!!errors.euid}
            aria-describedby={errors.euid ? 'euid-error' : 'euid-hint'}
          />
          {errors.euid ? (
            <p id="euid-error" className="text-sm text-destructive mt-1">
              {errors.euid.message}
            </p>
          ) : (
            <p id="euid-hint" className="text-sm text-muted-foreground mt-1">
              Mulesoft organisation EUID: MB_ followed by 18 alphanumeric characters
              {isAdmin ? ' (optional)' : ''}
            </p>
          )}
        </div>
      </div>
    </form>
  );
}
